import { X, Lock, Check } from 'lucide-react';

interface UpgradePromptProps {
  isOpen: boolean;
  onClose: () => void;
  feature?: string;
  reason?: string;
}

const FEATURE_DETAILS: Record<string, { name: string; plan: string; benefits: string[] }> = {
  udaap_scoring: {
    name: 'UDAAP Language Risk Detection',
    plan: 'Professional',
    benefits: [
      'Automated detection of unfair, deceptive, or abusive language',
      'Risk scoring for marketing materials',
      'Regulatory guidance references',
      'Examiner-ready documentation',
    ],
  },
  approval_workflows: {
    name: 'Ad Approval Workflows',
    plan: 'Professional',
    benefits: [
      'Multi-step approval process',
      'Role-based permissions',
      'Approval history tracking',
      'Email notifications',
    ],
  },
  audit_logs: {
    name: 'Immutable Audit Trail',
    plan: 'Professional',
    benefits: [
      'Complete activity history',
      'Tamper-proof logging',
      'Examiner-ready reports',
      'Compliance evidence',
    ],
  },
  version_tracking: {
    name: 'Ad Version Change Tracking',
    plan: 'Professional',
    benefits: [
      'Track all changes to ads over time',
      'Compare versions side-by-side',
      'Audit trail of modifications',
      'Regulatory documentation',
    ],
  },
  examiner_export: {
    name: 'Examiner-Ready Exports',
    plan: 'Professional',
    benefits: [
      'Formatted for regulatory review',
      'Complete audit documentation',
      'Professional PDF reports',
      'Evidence package generation',
    ],
  },
  hmda_validation: {
    name: 'HMDA LAR Validation',
    plan: 'Enterprise',
    benefits: [
      '160+ edit checks with plain-English explanations',
      'Examiner-ready PDF validation reports',
      'Fix suggestions with documented cause analysis',
      'Three views: by severity, by loan, by edit code',
    ],
  },
  advanced_udaap: {
    name: 'Advanced UDAAP Analysis',
    plan: 'Enterprise',
    benefits: [
      'Deep pattern recognition',
      'Contextual risk assessment',
      'Historical compliance trends',
      'Predictive risk modeling',
    ],
  },
  fair_lending: {
    name: 'Fair Lending Signals',
    plan: 'Enterprise',
    benefits: [
      'Fair lending risk detection',
      'Discriminatory language flagging',
      'ECOA compliance checks',
      'Fair Housing Act review',
    ],
  },
  ad_metadata: {
    name: 'Ad Placement & CRA Metadata',
    plan: 'Professional',
    benefits: [
      'Track ad medium, geography, and demographics',
      'Assessment area and MMCT tracking for CRA exams',
      'Auto-populated fields from AI scan results',
      'Maps directly to examiner spreadsheet columns',
    ],
  },
  review_comments: {
    name: 'Review Comments & Feedback',
    plan: 'Professional',
    benefits: [
      'Threaded comment system on reports',
      'Link comments to specific violations',
      'Resolve/unresolve workflow for tracking fixes',
      'Append-only audit trail for examiner defensibility',
    ],
  },
  examiner_xlsx: {
    name: 'Examiner XLSX Spreadsheet Export',
    plan: 'Professional',
    benefits: [
      'Export matching examiner template format exactly',
      'Marketing + Outreach sheets auto-populated',
      'Includes ad metadata, scan results, and comments',
      'Ready for CRA and Fair Lending exam submission',
    ],
  },
  compliance_checklists: {
    name: 'Compliance Checklists',
    plan: 'Enterprise',
    benefits: [
      'Create custom compliance review checklists',
      'Track checklist completion per report',
      'Category-based item organization',
      'Required vs optional item tracking',
    ],
  },
  exam_package: {
    name: 'One-Click Exam Packages',
    plan: 'Enterprise',
    benefits: [
      'Generate complete exam packages in one click',
      'ZIP with XLSX, PDFs, audit trails, and checklists',
      'Date range filtering for exam periods',
      'Everything examiners need in a single download',
    ],
  },
  exam_management: {
    name: 'Exam Center',
    plan: 'Enterprise',
    benefits: [
      'End-to-end exam lifecycle management',
      'Document request tracking & response monitoring',
      'One-click exam-ready ZIP packages',
      'Findings & remediation workflows with deadline tracking',
    ],
  },
  regulatory_intelligence: {
    name: 'Regulatory Monitoring',
    plan: 'Professional',
    benefits: [
      'Regulatory change monitoring & alerts',
      'State-specific compliance rules',
      'Prioritized action items with deadlines',
      'Multi-agency coverage (CFPB, FDIC, OCC, NCUA, Fed)',
    ],
  },
  enforcement_intelligence: {
    name: 'Enforcement Intelligence',
    plan: 'Enterprise',
    benefits: [
      'Enforcement action monitoring across FDIC, OCC, CFPB, NCUA, DOJ',
      '"Am I Exposed?" analysis against your operations',
      'Enforcement trend analysis and peer comparisons',
      'Prioritized alerts for relevant actions',
    ],
  },
  exam_readiness: {
    name: 'Exam Readiness Scoring',
    plan: 'Professional',
    benefits: [
      'Readiness scoring for upcoming exams',
      'Document completeness tracking',
      'Open findings and overdue monitoring',
      'AI-powered readiness recommendations',
    ],
  },
  edit_intelligence: {
    name: 'Edit Intelligence',
    plan: 'Enterprise',
    benefits: [
      'Root cause analysis for HMDA edit failures',
      'Pattern detection across validation runs',
      'Auto-fix suggestions for common errors',
      'Institutional trend tracking',
    ],
  },
  geocoding_validation: {
    name: 'Geocoding Validation',
    plan: 'Enterprise',
    benefits: [
      'Census tract validation against FFIEC data',
      'Geographic data integrity checks',
      'Tract-level demographic analysis',
      'Reference data management',
    ],
  },
  apor_rate_spread: {
    name: 'APOR Rate Spread Calculator',
    plan: 'Enterprise',
    benefits: [
      'Calculate rate spreads against current APOR tables',
      'Validate reported rate spreads across LAR entries',
      'Identify HOEPA and higher-priced loan triggers',
      'Rate table ingestion and management',
    ],
  },
  cfpb_filing: {
    name: 'CFPB Filing Preparation',
    plan: 'Enterprise',
    benefits: [
      'Filing workflow tracker with submission-ready exports',
      'Pre-submission validation checks',
      'Filing status monitoring',
      'Resubmission management',
    ],
  },
  lo_performance_monitoring: {
    name: 'Loan Officer Monitoring',
    plan: 'Enterprise',
    benefits: [
      'Per-loan-officer performance dashboards',
      'Denial rate and pricing disparity tracking by LO',
      'Demographic data collection completeness scoring',
      'Peer comparison and threshold alerts',
    ],
  },
  peer_benchmarking: {
    name: 'Peer Benchmarking',
    plan: 'Enterprise',
    benefits: [
      'Benchmark against CFPB data by asset size and charter type',
      'Peer group comparison analytics',
      'Industry trend analysis',
      'Board-ready benchmarking reports',
    ],
  },
  vendor_compliance: {
    name: 'Vendor Compliance',
    plan: 'Enterprise',
    benefits: [
      'Third-party risk management tracking',
      'Vendor due diligence documentation',
      'Compliance requirement mapping per vendor',
      'Review schedule management',
    ],
  },
};

export default function UpgradePrompt({ isOpen, onClose, feature, reason }: UpgradePromptProps) {
  if (!isOpen) return null;

  const featureDetails = feature ? FEATURE_DETAILS[feature] : null;
  const isGenericUpgrade = !feature || !featureDetails;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="w-full max-w-lg rounded-lg bg-white shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-200 p-6">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-100">
              <Lock className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                {isGenericUpgrade ? 'Upgrade Required' : 'Feature Not Available'}
              </h2>
              {featureDetails && (
                <p className="text-sm text-gray-600">{featureDetails.name}</p>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {reason && (
            <div className="mb-4 rounded-lg bg-yellow-50 p-4">
              <p className="text-sm text-yellow-800">{reason}</p>
            </div>
          )}

          {featureDetails ? (
            <>
              <p className="mb-4 text-gray-700">
                This feature is available on the <strong>{featureDetails.plan}</strong> plan.
              </p>

              <div className="mb-6 space-y-2">
                <p className="text-sm font-medium text-gray-900">Includes:</p>
                {featureDetails.benefits.map((benefit, index) => (
                  <div key={index} className="flex items-start gap-2">
                    <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-green-600" />
                    <span className="text-sm text-gray-700">{benefit}</span>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <p className="mb-6 text-gray-700">
              Upgrade your plan to access this feature and additional compliance capabilities.
            </p>
          )}

          {/* CTA Buttons */}
          <div className="flex gap-3">
            <a
              href="/contact"
              className="flex-1 rounded-lg bg-blue-600 px-4 py-2.5 text-center text-sm font-medium text-white hover:bg-blue-700"
            >
              Contact Sales
            </a>
            <button
              onClick={onClose}
              className="flex-1 rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Maybe Later
            </button>
          </div>

          {/* Contact option */}
          <p className="mt-4 text-center text-xs text-gray-500">
            Questions?{' '}
            <a href="/contact" className="text-blue-600 hover:text-blue-700">
              Contact our team
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
